import { v4 as uuidv4 } from "uuid";

import FullTemplate from "../../components/Assembler/JSON/FullTemplate";

import type { IStudio } from "../../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import {
  EStudioElementType,
  type IStudioElement,
} from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Elements.interface";

import type {
  IOverlayElement,
  IOverlayElementText,
  IStudioOverlay,
  IStudioOverlayCustom,
} from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

import {
  MaskType,
  type MaskComposition,
} from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Mask.interface";

import type { IStudioArea } from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Areas.interface";

import type { IVideoPreviewSource } from "../LivePreview/LivePreview.interface";

import type { IStudioWidget } from "../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import type { SceneTemplateResponse } from "../../interfaces/SceneTemplateApi";

import {
  currentSceneLang,
  localizeOverlay,
  resolveSceneText,
  type SceneLang,
} from "./sceneI18n";

/**
 * Materializa los `widgetRecipes` (placeholders de apply-time + uuids frescos)
 * a `IStudioWidget[]`. Compartido entre el path gráfico y el de "Fuentes".
 */
const materializeWidgetRecipes = (
  recipes: SceneTemplateResponse["widgetRecipes"] | undefined,
): IStudioWidget[] => {
  const pad2 = (value: number) => String(value).padStart(2, "0");
  const toLocalDateTimeString = (date: Date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
      date.getHours(),
    )}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
  const targetDate = toLocalDateTimeString(
    new Date(Date.now() + 5 * 60 * 1000),
  );
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const nowLocal = toLocalDateTimeString(new Date());
  const APPLY_TIME_PLACEHOLDERS: Record<string, string> = {
    __APPLY_TIME__: nowLocal,
    __APPLY_TIME_PLUS_5MIN__: targetDate,
  };

  return (recipes ?? []).map((r) => {
    const opts = { ...r.options } as Record<string, unknown>;
    for (const key of Object.keys(opts)) {
      const v = opts[key];
      if (typeof v === "string" && APPLY_TIME_PLACEHOLDERS[v] !== undefined) {
        opts[key] = APPLY_TIME_PLACEHOLDERS[v];
      }
    }
    if ((opts as { mode?: string }).mode === "countdown") {
      opts.targetDate = targetDate;
      if (opts.timezone === "UTC") opts.timezone = timezone;
    }
    return {
      id: uuidv4(),
      label: r.type,
      bounds: r.bounds,
      active: r.active,
      type: r.type,
      options: opts,
    } as IStudioWidget;
  });
};

/**
 * Textos que el template YA trae como `IStudioElement` (bounds en % y fontSize
 * en px de diseño sin unidad), p. ej. las escenas generadas con ai-overlays:
 * ahí los campos editables viajan en `studio.elements` y solo los rótulos
 * readOnly quedan en el chrome. Antes se descartaban y esos textos quedaban
 * huérfanos (zona vacía en la escena). Se conservan tal cual, activos, con ids
 * únicos frente a los promovidos desde el chrome (`el-<slug>-<n>`).
 */
const templateStudioElements = (
  template: SceneTemplateResponse,
  taken: ReadonlySet<string>,
): IStudioElement[] => {
  const used = new Set(taken);
  return (template.studio.elements ?? [])
    .filter(
      (el): el is IStudioElement =>
        !!el &&
        el.type === EStudioElementType.TEXT &&
        !!el.bounds &&
        !!el.options,
    )
    .map((el, idx) => {
      const base = el.id || `el-${template.slug}-tpl-${idx}`;
      let id = base;
      let suffix = 2;
      while (used.has(id)) id = `${base}-${suffix++}`;
      used.add(id);
      return {
        ...el,
        id,
        label: el.label || el.options.text || `text-${idx + 1}`,
        active: el.active !== false,
        options: {
          ...el.options,
          bold: el.options.bold ?? false,
          italic: el.options.italic ?? false,
          fontFamily: el.options.fontFamily ?? "Roboto",
        },
      };
    });
};

// zIndex del chrome de escena cuando se promueve a studio.mask. Entero (lo
// exige el z-index CSS del editor) y coincide con RenderFactory.MASK_BASE_Z /
// SceneMaskMount → queda debajo de las cámaras (que suelen estar en zIndex >= 1).
const FRAME_MASK_Z = 1;

const isFullCanvasBounds = (
  b: { left: number; top: number; width: number; height: number } | undefined,
): boolean =>
  !!b &&
  Math.abs(b.left) < 1 &&
  Math.abs(b.top) < 1 &&
  b.width >= 99 &&
  b.height >= 99;

/**
 * Detecta el "marco" decorativo full-canvas: overlay activo con elementos, SIN
 * `sourceSlot` y SIN fuente. Ese chrome no es un slot de fuente → va a
 * `studio.mask`, no a un área portadora. Un marco CON `sourceSlot` envuelve una
 * cámara (la encastra en el recorte) y se queda en el área.
 */
export const isDecorativeFrameArea = (area: IStudioArea): boolean => {
  const overlay = area.overlay;
  if (!overlay || !overlay.active) return false;
  const opts = overlay.options as
    | { elements?: unknown[]; sourceSlot?: unknown }
    | undefined;
  const hasElements = (opts?.elements?.length ?? 0) > 0;
  const hasSourceSlot = opts?.sourceSlot != null;
  const hasNoSource = area.media?.resource == null;
  return (
    hasElements &&
    !hasSourceSlot &&
    hasNoSource &&
    isFullCanvasBounds(area.bounds)
  );
};

/**
 * Levanta el marco decorativo full-canvas de `areas` a un `MaskComposition`
 * (studio.mask) y devuelve las áreas restantes SIN el portador. Si no hay
 * marco, devuelve las áreas tal cual. Reemplaza el patrón de "área-1 Marco".
 */
export const extractFrameToMask = (
  areas: IStudioArea[],
): { areas: IStudioArea[]; mask?: MaskComposition } => {
  const frameIdx = areas.findIndex(isDecorativeFrameArea);
  if (frameIdx === -1) return { areas };
  const frame = areas[frameIdx];
  const mask: MaskComposition = {
    type: MaskType.composition,
    overlay: frame.overlay as IStudioOverlayCustom,
    zIndex: frame.bounds?.zIndex ?? FRAME_MASK_Z,
  };
  return { areas: areas.filter((_, i) => i !== frameIdx), mask };
};

/**
 * Materializa una escena "Fuentes" (kind `sources`): preserva las áreas de
 * media del template, localiza los textos del overlay del marco, inyecta la
 * cámara por defecto en `area-cam` (si hay), y promueve el marco decorativo a
 * `studio.mask` soltando su área portadora.
 */
const buildSourcesStudioFromTemplate = (
  template: SceneTemplateResponse,
  lang: SceneLang,
  cameraResource?: IVideoPreviewSource | null,
): IStudio => {
  const src = template.studio;
  const localizedAreas = (src.areas ?? []).map((area): IStudioArea => {
    let next = area;
    if (area.overlay) {
      next = {
        ...next,
        overlay: localizeOverlay(area.overlay as IStudioOverlay, lang),
      };
    }
    if (area.id === "area-cam" && cameraResource) {
      next = { ...next, media: { ...next.media, resource: cameraResource } };
    }
    return next;
  });

  // El marco decorativo (area-1) deja de ser un área portadora: su chrome va a
  // studio.mask y el área se elimina. Las áreas de media (cámaras/pantalla) se
  // conservan. Un marco con sourceSlot (envuelve cámara) NO se mueve.
  const { areas, mask } = extractFrameToMask(localizedAreas);

  // Mask-native (seed migrado): el marco ya viene en `src.mask` → localizamos su
  // overlay (los marcos legacy ya se localizaron en el map de áreas antes de
  // extraerse en `mask`).
  const srcMask =
    src.mask?.type === MaskType.composition
      ? {
          ...src.mask,
          overlay: localizeOverlay(
            src.mask.overlay as IStudioOverlay,
            lang,
          ) as IStudioOverlayCustom,
        }
      : src.mask;

  return {
    ...src,
    id: `scene-${template.slug}`,
    areas,
    elements: templateStudioElements(template, new Set()),
    widgets: [
      ...(src.widgets ?? []),
      ...materializeWidgetRecipes(template.widgetRecipes),
    ],
    mask: mask ?? srcMask,
  };
};

/**
 * Maps a design aspect ratio token to its design-space pixel size. Scene
 * templates author their element bounds in these absolute px; the builder
 * converts them to percent.
 */
export const designSizeFor = (
  ratio: string | undefined,
): { width: number; height: number } => {
  switch (ratio) {
    case "4:3":
      return { width: 1920, height: 1440 };
    case "1:1":
      return { width: 1920, height: 1920 };
    case "21:9":
      return { width: 2520, height: 1080 };
    case "9:16":
      return { width: 1080, height: 1920 };
    case "16:9":
    case "free":
    default:
      return { width: 1920, height: 1080 };
  }
};

/**
 * Build a full-bleed scene (IStudio) from a `SceneTemplateResponse`. The
 * backend ships a pre-shaped `IStudio` with the visual elements parked in
 * `area-1.overlay.options.elements` at design-space pixel coords. At
 * apply-time we split:
 *
 *  · `text` elements (not `readOnly`) → promoted to first-class
 *    `IStudioElement`s on `studio.elements` with bounds converted to percent
 *    so the streamer can select / drag / re-style them from the Elements
 *    panel. `fontSize` stays in design-space px and `TextWidget` scales it.
 *  · Decorative chrome (shapes, gradients, glows, `readOnly` texts) stay
 *    inside `area-1.overlay` as decoration.
 *
 * Widget recipes are materialised onto `IStudio.widgets[]` with fresh uuids;
 * apply-time placeholders (`__APPLY_TIME__` / `__APPLY_TIME_PLUS_5MIN__`) are
 * resolved to real wall-clock timestamps so countdowns/progress bars tick
 * meaningfully right after apply.
 *
 * Extracted from ModalSceneTemplate so the scene-design picker (and any other
 * apply path) can reuse the exact same materialisation logic.
 */
export const buildStudioFromSceneTemplate = (
  template: SceneTemplateResponse,
  lang: SceneLang = currentSceneLang(),
  cameraResource?: IVideoPreviewSource | null,
): IStudio => {
  // Escenas "Fuentes": multi-área (marco + cámara/pantalla). No se promueven
  // los textos del chrome a IStudioElement editables — el marco queda intacto
  // en su overlay y la cámara se inyecta en `area-cam`; los `studio.elements`
  // que el template ya trae (textos editables) sí se conservan.
  if (template.kind === "sources") {
    return buildSourcesStudioFromTemplate(template, lang, cameraResource);
  }

  // Mask-native (seed migrado): el chrome viene en studio.mask. Legacy: en
  // areas[0].overlay (área portadora). Preferimos mask; el split de texto y el
  // output a studio.mask de abajo funcionan igual con cualquier fuente.
  const overlay =
    template.studio.mask?.type === MaskType.composition
      ? template.studio.mask.overlay
      : template.studio.areas?.[0]?.overlay;
  const allElements = ((
    overlay?.options as { elements?: IOverlayElement[] } | undefined
  )?.elements ?? []) as IOverlayElement[];

  const textElements = allElements.filter(
    (el): el is IOverlayElementText =>
      el.kind === "text" && el.options.readOnly !== true,
  );
  // Chrome decorativo (shapes/imágenes + textos readOnly). Los textos readOnly
  // se localizan in-place reescribiendo su `text`, así el render del overlay
  // (que lee options.text) sale en el idioma sin tocar el renderer.
  const decorElements: IOverlayElement[] = allElements
    .filter(
      (el) =>
        el.kind !== "text" ||
        (el.kind === "text" && el.options.readOnly === true),
    )
    .map((el) =>
      el.kind === "text"
        ? {
            ...el,
            options: {
              ...el.options,
              text: resolveSceneText(el.options, lang),
            },
          }
        : el,
    );

  const designSize = designSizeFor(template.designAspectRatio);

  const studioElements: IStudioElement[] = textElements.map((te, idx) => {
    const localizedText = resolveSceneText(te.options, lang);
    return {
      id: `el-${template.slug}-${idx}`,
      label: localizedText || `text-${idx + 1}`,
      bounds: {
        left: (te.bounds.left / designSize.width) * 100,
        top: (te.bounds.top / designSize.height) * 100,
        width: (te.bounds.width / designSize.width) * 100,
        height: (te.bounds.height / designSize.height) * 100,
        zIndex: te.bounds.zIndex ?? 10,
      },
      active: true,
      type: EStudioElementType.TEXT,
      options: {
        text: localizedText,
        fontSize: te.options.fontSize,
        fontColor: te.options.fontColor,
        fontGradient: te.options.fontGradient,
        bold: te.options.bold ?? false,
        italic: te.options.italic ?? false,
        textAlign: te.options.textAlign,
        fontFamily: te.options.fontFamily ?? "Roboto",
      },
    };
  });

  const templateElements = templateStudioElements(
    template,
    new Set(studioElements.map((el) => el.id)),
  );

  const decorOverlay = overlay
    ? {
        ...overlay,
        active: true,
        options: {
          ...(overlay.options as Record<string, unknown>),
          elements: decorElements,
        },
      }
    : undefined;

  const recipeWidgets = materializeWidgetRecipes(template.widgetRecipes);

  return {
    ...FullTemplate,
    // ID único y estable por escena. FullTemplate.id es '0' fijo, y `model.id`
    // se usa como IDENTIDAD de escena en varios lugares (ScenesManager
    // updateModelDatabase, StrategyDraw.getScene, ModalDesigner) además del path
    // del thumbnail (eriol-workspace://scenes/<model.id>.jpg). Sin esto las 3
    // escenas del diseño comparten '0' y se pisan entre sí (modelo + thumbnail).
    id: `scene-${template.slug}`,
    // El chrome decorativo va a studio.mask (full-canvas, sin área). La escena
    // gráfica no necesita área portadora → sin áreas. Los textos editables ya
    // se promovieron a studio.elements arriba.
    areas: [],
    elements: [...studioElements, ...templateElements],
    widgets: [...(FullTemplate.widgets ?? []), ...recipeWidgets],
    mask: decorOverlay
      ? {
          type: MaskType.composition,
          overlay: decorOverlay as IStudioOverlayCustom,
          zIndex: FullTemplate.areas[0]?.bounds?.zIndex ?? FRAME_MASK_Z,
        }
      : undefined,
  };
};
